import React, { useState } from 'react';
import { Globe, Lock, MoreVertical, Star, Users } from 'lucide-react';
import { formatDate, formatSize, kindOf } from '../../lib/files';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  ContextMenu, ContextMenuContent, ContextMenuEntries, ContextMenuLabel, ContextMenuTrigger,
} from '../ui/context-menu';

/**
 * Right-click on a row/tile: same actions as its ⋮ menu. Right-clicking an item that
 * isn't selected selects it (like Finder/Explorer); the event doesn't reach the
 * folder background menu behind it.
 */
const ItemContextMenu = ({ item, actions, selection, children }) => {
  const entries = actions.menu(item);
  if (!entries.length) return children;
  return (
    <ContextMenu>
      <ContextMenuTrigger
        asChild
        onContextMenu={(e) => {
          e.stopPropagation();
          if (!selection.selected.has(item.id)) selection.click(item);
        }}
      >
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuLabel>{item.name}</ContextMenuLabel>
        <ContextMenuEntries entries={entries} />
      </ContextMenuContent>
    </ContextMenu>
  );
};

/** Amber folder: darker tab behind a lighter body, readable at 16px. */
export const FolderGlyph = ({ className = 'h-7 w-7' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M2 6.6A2.6 2.6 0 0 1 4.6 4h4.1c.7 0 1.4.3 1.9.8L12.1 6.3h7.3A2.6 2.6 0 0 1 22 8.9V10H2z" fill="#D98E0B" />
    <path d="M2 9.2A1.7 1.7 0 0 1 3.7 7.5h16.6A1.7 1.7 0 0 1 22 9.2v8.2A2.6 2.6 0 0 1 19.4 20H4.6A2.6 2.6 0 0 1 2 17.4z" fill="#F5B83D" />
  </svg>
);

const FOLDER_BADGES = {
  restricted: { icon: Lock, className: 'bg-ink text-ink-foreground', label: 'Restricted' },
  external: { icon: Globe, className: 'bg-external text-white', label: 'Shared outside LampTwist' },
  shared: { icon: Users, className: 'bg-card text-foreground ring-1 ring-border', label: 'Shared with people' },
};

export const FileTag = ({ item, size = 'sm' }) => {
  if (item?.kind === 'folder') {
    const badge = FOLDER_BADGES[item.access];
    const large = size === 'lg';
    return (
      <span className={`relative flex shrink-0 items-center justify-center ${large ? 'h-full w-full rounded-lg bg-muted/50' : 'h-7 w-7'}`} aria-hidden="true">
        <FolderGlyph className={large ? 'h-16 w-16' : 'h-7 w-7'} />
        {badge && (
          <span
            title={badge.label}
            className={`absolute flex items-center justify-center rounded-full ${badge.className} ${large ? 'bottom-[22%] right-[30%] h-6 w-6' : '-bottom-0.5 -right-0.5 h-3.5 w-3.5'}`}
          >
            <badge.icon className={large ? 'h-3.5 w-3.5' : 'h-2 w-2'} strokeWidth={2.5} />
          </span>
        )}
      </span>
    );
  }
  const kind = kindOf(item);
  const box = size === 'lg' ? 'h-full w-full rounded-lg text-lg' : 'h-7 w-7 rounded-md text-[9.5px]';
  return (
    <span className={`flex shrink-0 items-center justify-center font-bold tracking-wide ${kind.tone} ${box}`} aria-hidden="true">
      {kind.tag}
    </span>
  );
};

export const AccessBadge = ({ item }) => {
  if (item.access === 'restricted') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
        <Lock className="h-3 w-3" aria-hidden="true" /> Restricted
      </span>
    );
  }
  if (item.access === 'external') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-external-soft px-1.5 py-0.5 text-xs font-medium text-external">
        <Globe className="h-3 w-3" aria-hidden="true" /> External
      </span>
    );
  }
  if (item.access === 'shared') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
        <Users className="h-3 w-3" aria-hidden="true" /> Shared
      </span>
    );
  }
  // Not shared with anyone: in My Drive that means only you can see it
  return <span className="text-xs text-muted-foreground">{item.drive_kind === 'personal' ? 'Only you' : 'Team'}</span>;
};

const ItemMenu = ({ item, actions }) => {
  const entries = actions.menu(item);
  if (!entries.length) return null;
  return (
    // Non-modal: a modal menu that opens a dialog leaves the page unclickable once the dialog closes
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for ${item.name}`}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground opacity-60 transition hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        {entries.map((entry, i) => (entry === '-'
          ? <DropdownMenuSeparator key={`sep-${i}`} />
          : (
            <DropdownMenuItem
              key={entry.label}
              onSelect={entry.run}
              className={`gap-2 py-2 ${entry.danger ? 'text-destructive focus:text-destructive' : ''}`}
            >
              <entry.icon className="h-4 w-4" /> {entry.label}
            </DropdownMenuItem>
          )))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Selection model: click selects, ⌘/Ctrl-click toggles, Shift-click selects a
 * range, double-click / Enter opens.
 */
export const useSelection = (items) => {
  const [selected, setSelected] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);

  const ids = items.map((i) => i.id);
  const live = new Set([...selected].filter((id) => ids.includes(id)));

  const click = (item, e) => {
    if (e?.shiftKey && anchor) {
      const a = ids.indexOf(anchor);
      const b = ids.indexOf(item.id);
      if (a >= 0 && b >= 0) {
        const [from, to] = a < b ? [a, b] : [b, a];
        setSelected(new Set(ids.slice(from, to + 1)));
        return;
      }
    }
    if (e?.metaKey || e?.ctrlKey) {
      const next = new Set(live);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      setSelected(next);
    } else {
      setSelected(new Set([item.id]));
    }
    setAnchor(item.id);
  };

  return {
    selected: live,
    selectedItems: items.filter((i) => live.has(i.id)),
    click,
    clear: () => setSelected(new Set()),
    selectAll: () => setSelected(new Set(ids)),
  };
};

const DragProps = (item, { onMoveInto, selection }) => {
  if (!onMoveInto) return {};
  const props = {
    draggable: item.role >= 20,
    onDragStart: (e) => {
      const ids = selection.selected.has(item.id) ? [...selection.selected] : [item.id];
      e.dataTransfer.setData('application/x-drive-nodes', JSON.stringify(ids));
      e.dataTransfer.effectAllowed = 'move';
    },
  };
  if (item.kind === 'folder') {
    Object.assign(props, {
      onDragOver: (e) => {
        if (e.dataTransfer.types.includes('application/x-drive-nodes')) {
          e.preventDefault();
          e.currentTarget.dataset.dropping = 'true';
        }
      },
      onDragLeave: (e) => { delete e.currentTarget.dataset.dropping; },
      onDrop: (e) => {
        delete e.currentTarget.dataset.dropping;
        const raw = e.dataTransfer.getData('application/x-drive-nodes');
        if (!raw) return;
        e.preventDefault();
        e.stopPropagation();
        const ids = JSON.parse(raw).filter((id) => id !== item.id);
        if (ids.length) onMoveInto(ids, item);
      },
    });
  }
  return props;
};

const ListView = ({ items, selection, actions, showLocation, onMoveInto }) => (
  // `relative` keeps the header's screen-reader labels inside the scroller on narrow screens
  <div className="relative -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
    <table className="w-full min-w-[680px] border-collapse text-left text-[13.5px]">
      <thead>
        <tr className="border-b border-border text-[12.5px] text-muted-foreground">
          <th scope="col" className="w-12 py-2 pl-2"><span className="sr-only">Type</span></th>
          <th scope="col" className="py-2 pr-3">Name</th>
          <th scope="col" className="w-[18%] py-2 pr-3">{showLocation ? 'Location' : 'Owner'}</th>
          <th scope="col" className="w-[16%] py-2 pr-3">Modified</th>
          <th scope="col" className="w-24 py-2 pr-3">Size</th>
          <th scope="col" className="w-32 py-2 pr-3">Access</th>
          <th scope="col" className="w-14 py-2 pr-3"><span className="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const isSelected = selection.selected.has(item.id);
          return (
            <ItemContextMenu key={item.id} item={item} actions={actions} selection={selection}>
            <tr
              aria-selected={isSelected}
              onClick={(e) => selection.click(item, e)}
              onDoubleClick={() => actions.open(item)}
              {...DragProps(item, { onMoveInto, selection })}
              className={`group cursor-default border-b border-border/70 transition-colors data-[dropping=true]:bg-accent data-[dropping=true]:outline data-[dropping=true]:outline-2 data-[dropping=true]:outline-filament ${
                isSelected ? 'bg-accent' : 'hover:bg-muted/60'
              }`}
            >
              <td className="py-2 pl-2"><FileTag item={item} /></td>
              <td className="max-w-0 py-2 pr-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); selection.click(item, e); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); actions.open(item); } }}
                    className="truncate rounded text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title={item.name}
                  >
                    {item.name}
                  </button>
                  {item.starred && <Star className="h-3.5 w-3.5 shrink-0 fill-filament text-filament" aria-label="Starred" />}
                </div>
              </td>
              <td className="max-w-0 truncate py-2 pr-3 text-muted-foreground">
                {showLocation ? (item.parent_name ? `${item.drive_name} / ${item.parent_name}` : item.drive_name) : (item.created_by?.name || '—')}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-muted-foreground">{formatDate(item.trashed_at || item.modified_at)}</td>
              <td className="py-2 pr-3 tabular text-xs text-muted-foreground">{item.kind === 'folder' ? '—' : formatSize(item.size)}</td>
              <td className="py-2 pr-3"><AccessBadge item={item} /></td>
              <td className="py-2 pr-3"><ItemMenu item={item} actions={actions} /></td>
            </tr>
            </ItemContextMenu>
          );
        })}
      </tbody>
    </table>
  </div>
);

const GridView = ({ items, selection, actions, onMoveInto }) => (
  <ul className="grid grid-cols-[repeat(auto-fill,minmax(168px,1fr))] gap-3">
    {items.map((item) => {
      const isSelected = selection.selected.has(item.id);
      return (
        <ItemContextMenu key={item.id} item={item} actions={actions} selection={selection}>
        <li
          aria-selected={isSelected}
          onClick={(e) => selection.click(item, e)}
          onDoubleClick={() => actions.open(item)}
          {...DragProps(item, { onMoveInto, selection })}
          className={`group relative flex flex-col overflow-hidden rounded-xl transition data-[dropping=true]:outline data-[dropping=true]:outline-2 data-[dropping=true]:outline-filament ${
            isSelected ? 'bg-accent ring-1 ring-filament' : 'border border-border hover:bg-muted/50'
          }`}
        >
          <div className="h-[96px] p-1.5 pb-0"><FileTag item={item} size="lg" /></div>
          <div className="flex items-center gap-1 py-2 pl-3 pr-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); selection.click(item, e); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); actions.open(item); } }}
              className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={item.name}
            >
              <span className="block truncate text-[13.5px] font-medium">{item.name}</span>
              <span className="block text-xs text-muted-foreground">{formatDate(item.modified_at)}</span>
            </button>
            <ItemMenu item={item} actions={actions} />
          </div>
        </li>
        </ItemContextMenu>
      );
    })}
  </ul>
);

const ItemsView = ({ view = 'list', ...props }) =>
  (view === 'grid' ? <GridView {...props} /> : <ListView {...props} />);

export default ItemsView;
